import { useCallback, useEffect, useState } from 'react'
import { collection, doc, getDoc, onSnapshot, updateDoc } from 'firebase/firestore'
import db from '../../../../services/firebase'
import { calculateDocumentStatus, getControls, updateControlStatus } from './controlUtils'

/**
 * Custom hook to manage tasks/activities
 * @param currentUser The current authenticated user
 * @returns Tasks and getActivities function
 */
export const useActivities = (currentUser: any) => {
  const [tasks, setTasks] = useState<any[]>([])

  const getActivities = useCallback(() => {
    try {
      const tenantId = currentUser?.tenantId || ''
      if (!tenantId) {
        return
      }
      const tenantRef = doc(db, 'tenants', tenantId)
      const controlsCollectionRef = collection(tenantRef, 'tasklists')

      const unsubscribe = onSnapshot(controlsCollectionRef, (snapshot) => {
        const controlsData = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        }))
        setTasks(controlsData)
      })

      return unsubscribe
    } catch (error) {
      console.error('Error getting controls:', error)
    }
  }, [currentUser])

  useEffect(() => {
    const unsubscribe = getActivities()
    return () => {
      if (unsubscribe) {
        unsubscribe()
      }
    }
  }, [getActivities])

  return { tasks, getActivities }
}

/**
 * Custom hook to manage audits
 * @param currentUser The current authenticated user
 * @param currentAuditContext The current audit context
 * @returns Audits state
 */
export const useAudits = (currentUser: any, currentAuditContext: any) => {
  const [audits, setAudits] = useState<any[]>([])

  useEffect(() => {
    const tenantId = currentUser?.tenantId || ''
    if (!tenantId) {
      return
    }
    const tenantRef = doc(db, 'tenants', tenantId)
    const PolicyRef = collection(tenantRef, 'audits')

    const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
      let Data = snapshot.docs
        .map((doc) => ({
          ...doc.data(),
          id: doc.id,
          status: doc.data()?.status,
          createdAt: doc.data().createdAt,
          auditor: doc.data().auditor,
        }))
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .filter((audit) => audit.status === 'Ongoing')

      if (currentUser?.role === 'Auditor') {
        Data = Data.filter((audit) => {
          if (!audit.auditor) return false
          
          if (Array.isArray(audit.auditor)) {
            return audit.auditor.some(auditor => 
              auditor.id === currentUser?.uid || auditor.email === currentUser?.email
            )
          } else {
            return audit.auditor.id === currentUser?.uid || audit.auditor.email === currentUser?.email
          }
        })
        setAudits(Data)
      }

      if (currentAuditContext) {
        Data = Data.filter((audit) => audit.id === currentAuditContext.auditId)
        setAudits(Data)
      }
    })
    setAudits([])
    return unsubscribe
  }, [currentAuditContext, currentUser])

  return { audits }
}

/**
 * Custom hook to manage control tests
 * @param id The control ID
 * @param controls The controls array
 * @param tests The tests array
 * @param showDeactivated Whether to show deactivated tests
 * @returns Specific tests and documents
 */
export const useControlTests = (id: string, controls: any[], tests: any[], showDeactivated: boolean) => {
  const [specificTests, setSpecificTests] = useState<any[]>([])
  const [specificdocuments, setSpecificdocuments] = useState<any[]>([])
  const [deactivatedCount, setDeactivatedCount] = useState(0)

  // Effect for specific tests
  useEffect(() => {
    const testIdss = controls.filter((doc) => doc.id === id).flatMap((item) => item.test_id)
    const testControl = tests
      .filter((item) => (showDeactivated ? true : item.status !== 'Deactivated'))
      .filter((item) => testIdss.includes(item.id))
    setSpecificTests(testControl)
  }, [id, controls, tests, showDeactivated])

  // Effect for specific documents
  useEffect(() => {
    const docIdss = controls.filter((doc) => doc.id === id).flatMap((item) => item.document_id)
    const docControl = tests
      .filter((item) => (showDeactivated ? true : item.status !== 'Deactivated'))
      .filter((item) => docIdss.includes(item.id))
    setSpecificdocuments(docControl)
  }, [id, controls, tests, showDeactivated])

  // Effect for deactivated count
  useEffect(() => {
    const testIds = controls.filter((doc) => doc.id === id).flatMap((item) => item.test_id)
    const documentIds = controls.filter((doc) => doc.id === id).flatMap((item) => item.document_id)

    const deactivatedTests = tests
      .filter((item) => item.status === 'Deactivated')
      .filter((item) => testIds.includes(item.id))

    const deactivatedDocs = tests
      .filter((item) => item.status === 'Deactivated')
      .filter((item) => documentIds.includes(item.id))

    setDeactivatedCount(deactivatedTests.length + deactivatedDocs.length)
  }, [id, controls, tests])

  return { specificTests, specificdocuments, deactivatedCount }
}

/**
 * Custom hook to update control status based on tests and documents
 * @param id The control ID
 * @param controls The controls array
 * @param currentUser The current authenticated user
 * @param specificTests The specific tests array
 * @param specificdocuments The specific documents array
 */
export const useControlStatusUpdate = (
  id: string,
  controls: any[],
  currentUser: any,
  specificTests: any[],
  specificdocuments: any[]
) => {
  useEffect(() => {
    const control = controls.find((c) => c.id === id)
    if (control) {
      let controlTest = calculateDocumentStatus(specificTests, control.test_id) || '0/0'
      let controlDocument = calculateDocumentStatus(specificdocuments, control.document_id) || '0/0'

      controlTest = controlTest.trim() === 'N/A' ? '0/0' : controlTest
      controlDocument = controlDocument.trim() === 'N/A' ? '0/0' : controlDocument

      const [currentCount, totalCount] = controlTest.split('/').map(Number)
      const [currentCount1, totalCount1] = controlDocument.split('/').map(Number)

      const newStatus =
        (currentCount === totalCount && currentCount1 === totalCount1) ||
        (currentCount === 0 && totalCount === 0 && currentCount1 === 0 && totalCount1 === 0)
          ? 'OK'
          : 'Unhealthy'

      if (
        newStatus !== control.control_evidence_status &&
        control.control_evidence_status !== 'Deactivated'
      ) {
        const timeoutId = setTimeout(() => {
          updateControlStatus(control, currentUser, specificTests, specificdocuments)
        }, 1000)
        return () => clearTimeout(timeoutId)
      }
    }
  }, [specificTests, specificdocuments, id, controls, currentUser])
}

/**
 * Custom hook to update control document status
 * @param id The control ID
 * @param currentUser The current authenticated user
 * @param specificdocuments The specific documents array
 */
export const useUpdateControlDocument = (id: string, currentUser: any, specificdocuments: any[]) => {
  useEffect(() => {
    const updateDocument = async () => {
      if (!currentUser) return
      if (id) {
        const tenantId = currentUser.tenantId || ''
        if (!tenantId) {
          return
        }

        const controlDocRef = doc(db, `tenants/${tenantId}/controls/${id}`)
        const controlDocSnapshot = await getDoc(controlDocRef)

        if (controlDocSnapshot.exists()) {
          const controlData = controlDocSnapshot.data()

          const documentIds = controlData.document_id || []
          let passCount = 0
          const totalCount = documentIds.length

          for (const testId of documentIds) {
            try {
              const testDocRef = doc(db, `tenants/${tenantId}/tests/${testId}`)
              const testDocSnapshot = await getDoc(testDocRef)

              if (testDocSnapshot.exists()) {
                const testData = testDocSnapshot.data()
                if (testData.status === 'Pass') {
                  passCount++
                }
              }
            } catch (error) {
              console.error('Error fetching test document: ', error)
            }
          }

          const updatedControlDocument = `${passCount}/${totalCount}`
          try {
            await updateDoc(controlDocRef, {
              control_document: updatedControlDocument,
            })
          } catch (error) {
            console.error('Error updating control document: ', error)
          }
        }
      }
    }

    updateDocument()
  }, [specificdocuments, currentUser, id])
}

/**
 * Custom hook to update control test status
 * @param id The control ID
 * @param currentUser The current authenticated user
 * @param specificTests The specific tests array
 */
export const useUpdateControlTest = (id: string, currentUser: any, specificTests: any[]) => {
  useEffect(() => {
    const updateTest = async () => {
      if (!currentUser) return
      if (id) {
        const tenantId = currentUser.tenantId || ''
        if (!tenantId) {
          return
        }
        const controlDocRef = doc(db, `tenants/${tenantId}/controls/${id}`)
        const controlDocSnapshot = await getDoc(controlDocRef)

        if (controlDocSnapshot.exists()) {
          const controlData = controlDocSnapshot.data()
          const testIds = controlData.test_id || []
          let passCount = 0
          const totalCount = testIds.length

          for (const testId of testIds) {
            try {
              const testDocRef = doc(db, `tenants/${tenantId}/tests/${testId}`)
              const testDocSnapshot = await getDoc(testDocRef)

              if (testDocSnapshot.exists()) {
                const testData = testDocSnapshot.data()
                if (testData.status === 'Pass') {
                  passCount++
                }
              }
            } catch (error) {
              console.error('Error fetching test document: ', error)
            }
          }

          const updatedControlTest = `${passCount}/${totalCount}`
          try {
            await updateDoc(controlDocRef, {
              control_test: updatedControlTest,
            })
          } catch (error) {
            console.error('Error updating control document: ', error)
          }
        }
      }
    }

    updateTest()
  }, [specificTests, currentUser, id])
}

/**
 * Custom hook to load control data
 * @param id The control ID
 * @param currentUser The current authenticated user
 * @returns Controls and tests arrays, and loadControls function
 */
export const useLoadControls = (id: string, currentUser: any) => {
  const [controls, setControls] = useState<any[]>([])
  const [tests, setTests] = useState<any[]>([])

  const loadControls = useCallback(() => {
    if (!currentUser) return
    if (id) {
      getControls(currentUser, id, setControls, setTests)
    }
  }, [currentUser, id])

  useEffect(() => {
    loadControls()
  }, [loadControls])

  return { controls, tests, loadControls }
}

/**
 * Function to handle owner assignment
 * @param event The change event
 * @param id The control ID
 * @param currentUser The current authenticated user
 * @param setNewOwner State setter for new owner
 * @param personnels Array of personnel data to find UID
 */
export const handleOwnerAssignment = async (
  event: React.ChangeEvent<HTMLSelectElement>,
  id: string,
  currentUser: any,
  setNewOwner: React.Dispatch<React.SetStateAction<string>>,
  personnels: any[]
) => {
  const selectedOwner = event.target.value
  setNewOwner(selectedOwner)

  try {
    const tenantId = currentUser?.tenantId || ''
    const testDocRef = doc(db, `tenants/${tenantId}/controls/${id}`)
    
    const selectedPersonnel = personnels.find(person => person.full_name === selectedOwner)
    const ownerUid = selectedPersonnel?.uid || selectedPersonnel?.id || ""

    await updateDoc(testDocRef, {
      control_owner: selectedOwner,
      control_uid: ownerUid,
    })

    setNewOwner('')
  } catch (error) {
    console.error('Error assigning owner: ', error)
  }
}

/**
 * Function to create a formatted date and time string
 * @returns Object containing formatted date and time
 */
export const getFormattedDateTime = () => {
  const currentDate = new Date()

  const day = currentDate.getDate()
  const month = currentDate.toLocaleString('default', { month: 'long' })
  const year = currentDate.getFullYear()
  const formattedDate = `${day} ${month}, ${year}`

  let hours = currentDate.getHours()
  let minutes = currentDate.getMinutes()
  let ampm = hours >= 12 ? 'PM' : 'AM'

  minutes = Math.round(minutes / 12) * 12
  if (minutes === 60) {
    minutes = 0
    hours += 1
  }

  hours = hours % 12
  hours = hours ? hours : 12

  const formattedTime = `${hours}:${minutes.toString().padStart(2, '0')} ${ampm}`

  return { formattedDate, formattedTime }
}